import { Signal, signal, computed } from '@angular/core';

export interface AlertState {
  type: 'success' | 'warning' | 'error' | 'info';
  title?: string;
  message: string;
}

/**
 * Estado de un aviso pintado DENTRO del formulario.
 *
 * @deprecated Un aviso no pertenece al formulario que lo provoca, sino a la PANTALLA. Pintado
 * dentro compite con los campos por el mismo espacio, empuja el contenido al aparecer, y un CRUD
 * anidado no tiene donde ponerlo sin estorbar. Usar `NebulaToastService` del kit: se publica el
 * aviso y la zona de toast lo recoge, sin que quien avisa necesite saber donde esta.
 *
 * <p>Se conserva porque sesenta y seis plantillas del ERP siguen pintando su aviso dentro. No se
 * retira hasta que esas pantallas se pasen al toast; retirarlo antes las dejaria sin avisar.</p>
 */
export class AlertStore {
  private alertSignal = signal<AlertState | null>(null);
  private alertTimer: any = null;

  readonly alert: Signal<AlertState | null> = this.alertSignal.asReadonly();
  readonly showAlertSignal = computed(() => this.alertSignal() !== null);
  readonly alertType = computed(() => this.alertSignal()?.type ?? 'info');
  readonly alertTitle = computed(() => this.alertSignal()?.title);
  readonly alertMessage = computed(() => this.alertSignal()?.message ?? '');

  clearAlert(): void {
    if (this.alertTimer) {
      clearTimeout(this.alertTimer);
      this.alertTimer = null;
    }
    this.alertSignal.set(null);
  }

  showAlert(type: AlertState['type'], message: string, title?: string): void {
    const current = this.alertSignal()?.type;
    if (current === 'error' && type !== 'error') return;

    if (this.alertTimer) {
      clearTimeout(this.alertTimer);
      this.alertTimer = null;
    }

    this.alertSignal.set({ type, title, message });

    if (type === 'success' || type === 'info') {
      this.alertTimer = setTimeout(() => {
        this.clearAlert();
      }, 5000);
    }
  }

  setError(message: string, title = 'Error'): void {
    this.showAlert('error', message, title);
  }

  setSuccess(message: string, title = 'Éxito'): void {
    this.showAlert('success', message, title);
  }

  setWarning(message: string, title = 'Advertencia'): void {
    this.showAlert('warning', message, title);
  }

  setInfo(message: string, title = 'Información'): void {
    this.showAlert('info', message, title);
  }
}
